import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Create as PenIcon,
} from '@mui/icons-material';
import { calculateOutlay } from '../../../../utils/stampDutyRates';
import type { PublicSitePlanPayload } from '../../../services/superPickerPublicApi';
import { fetchPublicSitePlan } from '../../../services/superPickerPublicApi';
import type { PublicUnitForMenu } from './types';
import { buildPublicUnitSyntheticKey } from './types';
import FloorPlanComparisonViewer, {
  type CompareQuickRefColumn,
  type PublicFloorPlanToCompare,
} from '../floorplan/FloorPlanComparisonViewer';
import FullVersionTooltip, { FULL_VERSION_CAPTION, GATED_DISABLED_SX, GATED_DISABLED_ACCENT_SX } from '../FullVersionTooltip';
import SitePlanComparisonViewer, {
  type PublicSitePlanCompareColumn,
} from '../floorplan/SitePlanComparisonViewer';
import { useRevealedPrices } from '../../../services/revealedPrices';
import { isGroundFloorNumber, parseUnitNumber as parseUnitNumberShared } from '../../../utils/parseUnitNumber';

/**
 * Public-side floor-plan comparison dialog. Mirrors the agent-side
 * compare dialog's TABLE view (see `SuperPickerApp.tsx` around
 * `compareDialogFloorPlans` rendering) — image header per unit, then
 * an OVERVIEW grid with Price / PSF / Area / Bedroom / Bathroom /
 * Block / Floor / Stack. On mobile (`sm` and below) the grid collapses
 * into stacked per-unit cards with label/value rows so the overview
 * stays readable without horizontal scrolling. Differences:
 *
 *   - Price / PSF cells render the masked `priceDisplay` / `psfDisplay`
 *     strings the backend emits, unless the recipient has already
 *     revealed that unit (in which case the unmasked dollar string
 *     produced from `RevealedPricesContext` is substituted in).
 *   - Clicking a floor-plan thumbnail opens the expanded
 *     `FloorPlanComparisonViewer` (agent parity with
 *     `compareExpandedView` in `SuperPickerApp.tsx`).
 *   - Export PDF is gated in the public app with an inline full-version caption.
 */

interface FloorPlanCompareModalProps {
  open: boolean;
  cart: PublicUnitForMenu[];
  onClose: () => void;
  onRemove: (unit: PublicUnitForMenu) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
  onClear: () => void;
}

/** Agent-default mortgage assumptions for outlay / installment rows. */
const DEFAULT_LOAN_TO_VALUE = 75;
const DEFAULT_TENURE_YEARS = 30;
const DEFAULT_INTEREST_RATE = 1.55;

const calculateInstallment = (
  price: number,
  loanToValuePercent: number,
  tenureYears: number,
  interestRatePercent: number,
): number => {
  if (price <= 0 || loanToValuePercent <= 0 || tenureYears <= 0) return 0;
  const principal = price * (loanToValuePercent / 100);
  const monthlyRate = interestRatePercent / 100 / 12;
  const numberOfPayments = tenureYears * 12;
  if (monthlyRate === 0) return principal / numberOfPayments;
  return (
    (principal * (monthlyRate * (1 + monthlyRate) ** numberOfPayments))
    / ((1 + monthlyRate) ** numberOfPayments - 1)
  );
};

/**
 * Format a revealed dollar amount for display. Mirrors the helper inside
 * `FloorPlanModal` so unmasked prices in the cart look identical to the
 * unmasked prices in the single-unit floor-plan view.
 */
const formatRevealedDollar = (value: number | null | undefined): string | null => {
  if (value == null || !Number.isFinite(value) || value <= 0) return null;
  return `$${Math.round(value).toLocaleString('en-SG')}`;
};

const formatArea = (sqft: number | null | undefined): string => {
  if (sqft == null || !Number.isFinite(sqft) || sqft <= 0) return '-';
  return `${Math.round(sqft).toLocaleString('en-SG')} sqft`;
};

/**
 * Parse `#floor-stack` for compare overview. Ground ("G") floors display
 * as missing (UI shows "-"); stack still comes from the G rewrite.
 */
const parseUnitNumber = (
  unitNumber: string | null | undefined,
  floorNumber?: string | null,
): { floor: number | null; stack: number | null } => {
  const parsed = parseUnitNumberShared(unitNumber, floorNumber);
  if (isGroundFloorNumber(floorNumber)) {
    return { floor: null, stack: parsed.stack };
  }
  return { floor: parsed.floor, stack: parsed.stack };
};

const parseDollarString = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const m = value.match(/^\$([0-9,]+)$/);
  if (!m) return null;
  const n = parseInt(m[1].replace(/,/g, ''), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * One column's worth of resolved data for a unit in the cart. Each row
 * of the OVERVIEW table indexes into this — `priceDisplay` / `psfDisplay`
 * already carry the revealed-or-masked string the cell should render.
 */
interface CompareColumn {
  unit: PublicUnitForMenu;
  isSold: boolean;
  priceDisplay: string;
  psfDisplay: string;
  outlayDisplay: string;
  installmentDisplay: string;
  priceNumeric: number | null;
  psfNumeric: number | null;
  outlayNumeric: number | null;
  installmentNumeric: number | null;
  floor: number | null;
  stack: number | null;
}

type RowKey =
  | 'price'
  | 'psf'
  | 'outlay'
  | 'installment'
  | 'areaSqft'
  | 'bedroom'
  | 'bathroom'
  | 'block'
  | 'floor'
  | 'stack';

interface OverviewRow {
  key: RowKey;
  label: string;
}

const OVERVIEW_ROWS: OverviewRow[] = [
  { key: 'price', label: 'Price' },
  { key: 'psf', label: 'PSF' },
  { key: 'outlay', label: 'Outlay' },
  { key: 'installment', label: 'Installment' },
  { key: 'areaSqft', label: 'Area' },
  { key: 'bedroom', label: 'Bedroom' },
  { key: 'bathroom', label: 'Bathroom' },
  { key: 'block', label: 'Block' },
  { key: 'floor', label: 'Floor' },
  { key: 'stack', label: 'Stack' },
];

const BIGGER_BETTER: Record<RowKey, boolean> = {
  price: false,
  psf: false,
  outlay: false,
  installment: false,
  areaSqft: true,
  bedroom: true,
  bathroom: true,
  block: false,
  floor: true,
  stack: false,
};
const COMPARABLE_KEYS: RowKey[] = [
  'price',
  'psf',
  'outlay',
  'installment',
  'areaSqft',
  'bedroom',
  'bathroom',
  'floor',
];

/**
 * Compute the cell display string for a row + column. SOLD units
 * render "SOLD" on the price/PSF rows (matches the agent layout) and
 * fall through to their normal values for everything else.
 */
const cellDisplay = (col: CompareColumn, key: RowKey): string => {
  const priceHidden = col.isSold;
  if (priceHidden && (key === 'price' || key === 'psf' || key === 'outlay' || key === 'installment')) {
    return '-';
  }
  switch (key) {
    case 'price': return col.priceDisplay || '-';
    case 'psf': return col.psfDisplay || '-';
    case 'outlay': return col.outlayDisplay || '-';
    case 'installment': return col.installmentDisplay || '-';
    case 'areaSqft': return formatArea(col.unit.areaSqft);
    case 'bedroom': return col.unit.bedroom != null ? String(col.unit.bedroom) : '-';
    case 'bathroom': return col.unit.bathroom != null ? String(col.unit.bathroom) : '-';
    case 'block': return col.unit.blockNumber || '-';
    case 'floor': return col.floor != null ? String(col.floor) : '-';
    case 'stack': return col.stack != null ? String(col.stack) : '-';
    default: return '-';
  }
};

/**
 * Numeric value used for best-value highlighting. Returns `null` when
 * the row isn't comparable for this column (sold units' prices, masked
 * placeholders without a parseable `$N,NNN` shape, missing fields).
 */
const cellNumeric = (col: CompareColumn, key: RowKey): number | null => {
  if (col.isSold && (key === 'price' || key === 'psf' || key === 'outlay' || key === 'installment')) {
    return null;
  }
  switch (key) {
    case 'price': return col.priceNumeric;
    case 'psf': return col.psfNumeric;
    case 'outlay': return col.outlayNumeric;
    case 'installment': return col.installmentNumeric;
    case 'areaSqft': return col.unit.areaSqft != null && col.unit.areaSqft > 0 ? col.unit.areaSqft : null;
    case 'bedroom': return col.unit.bedroom != null && col.unit.bedroom > 0 ? col.unit.bedroom : null;
    case 'bathroom': return col.unit.bathroom != null && col.unit.bathroom > 0 ? col.unit.bathroom : null;
    case 'floor': return col.floor;
    default: return null;
  }
};

/** Floor plan / site plan mode switcher shown in the compare dialog footer. */
const VisualizerModeToggle: React.FC<{
  value: 'floorplan' | 'siteplan';
  onChange: (value: 'floorplan' | 'siteplan') => void;
}> = ({ value, onChange }) => (
  <ToggleButtonGroup
    value={value}
    exclusive
    onChange={(_, next) => {
      if (next != null) onChange(next);
    }}
    size="small"
    sx={{
      flexShrink: 0,
      '& .MuiToggleButton-root': { px: 1.2, textTransform: 'none' },
    }}
  >
    <ToggleButton value="floorplan">Floor Plan</ToggleButton>
    <ToggleButton value="siteplan">Site Plan</ToggleButton>
  </ToggleButtonGroup>
);

export const FloorPlanCompareModal: React.FC<FloorPlanCompareModalProps> = ({
  open,
  cart,
  onClose,
  onRemove,
  onMove: _onMove,
  onClear,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Read the full reveals cache so we can substitute the unmasked
  // price/psf for any cart unit whose owner has already paid to reveal
  // it (in the per-row menu / floor-plan modal). Without this, the cart
  // would keep showing the masked snapshot captured at "add to compare"
  // time even when the unit is unmasked elsewhere on the page.
  const { store: revealedStore } = useRevealedPrices();
  /** Expanded side-by-side floor-plan viewer (agent `compareExpandedView`). */
  const [expandedView, setExpandedView] = useState(false);
  /** Floor plan vs site plan toggle. */
  const [visualizerMode, setVisualizerMode] = useState<'floorplan' | 'siteplan'>('floorplan');
  /** Table-view thumbnail zoom (agent `compareFloorPlanZoom`). */
  const [floorPlanZoom, setFloorPlanZoom] = useState(1);
  const [showSitePlanPoints, setShowSitePlanPoints] = useState(true);
  const [sitePlansByProjectId, setSitePlansByProjectId] = useState<
    Map<string, PublicSitePlanPayload | null>
  >(new Map());
  const [sitePlansLoading, setSitePlansLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setExpandedView(false);
      setVisualizerMode('floorplan');
      setFloorPlanZoom(1);
    }
  }, [open]);

  useEffect(() => {
    if (cart.length === 0) {
      setExpandedView(false);
      setVisualizerMode('floorplan');
    }
  }, [cart.length]);

  /**
   * Resolve every cart entry into a fully-decided `CompareColumn` —
   * price / psf strings already substituted with revealed values where
   * available, floor / stack already parsed, sold flag normalised. This
   * is the single source of truth for both the on-screen table and the
   * exported PDF, so the two stay in lock-step.
   */
  const columns = useMemo<CompareColumn[]>(() => {
    return cart.map((unit) => {
      const revealedRow = unit.projectId
        ? revealedStore.towerView[unit.projectId]?.[
            buildPublicUnitSyntheticKey(unit.blockNumber, unit.unitNumber)
          ]
        : undefined;
      const priceDisplay = formatRevealedDollar(revealedRow?.price) ?? unit.priceDisplay ?? '-';
      const psfDisplay = formatRevealedDollar(revealedRow?.psf) ?? unit.psfDisplay ?? '-';
      const isSold = (unit.unitStatus ?? '').toString().toLowerCase() === 'sold';
      const priceNumeric = parseDollarString(priceDisplay);
      const outlayNumeric =
        !isSold && priceNumeric != null
          ? calculateOutlay(priceNumeric, DEFAULT_LOAN_TO_VALUE / 100)
          : null;
      const installmentNumeric =
        !isSold && priceNumeric != null
          ? calculateInstallment(
              priceNumeric,
              DEFAULT_LOAN_TO_VALUE,
              DEFAULT_TENURE_YEARS,
              DEFAULT_INTEREST_RATE,
            )
          : null;
      const { floor, stack } = parseUnitNumber(unit.unitNumber, unit.floorNumber);
      return {
        unit,
        isSold,
        priceDisplay,
        psfDisplay,
        outlayDisplay: outlayNumeric != null ? formatRevealedDollar(outlayNumeric) ?? '-' : '-',
        installmentDisplay:
          installmentNumeric != null ? formatRevealedDollar(installmentNumeric) ?? '-' : '-',
        priceNumeric,
        psfNumeric: parseDollarString(psfDisplay),
        outlayNumeric,
        installmentNumeric,
        floor,
        stack,
      };
    });
  }, [cart, revealedStore]);

  const viewerFloorPlans = useMemo<PublicFloorPlanToCompare[]>(
    () =>
      columns
        .filter((c) => Boolean(c.unit.floorplan))
        .map((c) => ({
          unit: c.unit,
          imageUrl: c.unit.floorplan as string,
        })),
    [columns],
  );

  const quickRefColumns = useMemo<CompareQuickRefColumn[]>(
    () =>
      columns.map((c) => ({
        unit: c.unit,
        priceDisplay: c.isSold ? 'SOLD' : c.priceDisplay || '-',
        psfDisplay: c.isSold ? 'SOLD' : c.psfDisplay || '-',
        areaDisplay: formatArea(c.unit.areaSqft),
        bedroomDisplay: c.unit.bedroom != null ? String(c.unit.bedroom) : '-',
        bathroomDisplay: c.unit.bathroom != null ? String(c.unit.bathroom) : '-',
      })),
    [columns],
  );

  useEffect(() => {
    if (!open || visualizerMode !== 'siteplan') return;
    const projectIds = Array.from(
      new Set(
        columns
          .map((c) => c.unit.projectId)
          .filter((id): id is string => Boolean(id)),
      ),
    );
    const missing = projectIds.filter((id) => !sitePlansByProjectId.has(id));
    if (missing.length === 0) return;

    let cancelled = false;
    setSitePlansLoading(true);
    Promise.all(
      missing.map(async (projectId) => {
        try {
          const sitePlan = await fetchPublicSitePlan(projectId);
          return [projectId, sitePlan] as const;
        } catch {
          return [projectId, null] as const;
        }
      }),
    )
      .then((entries) => {
        if (cancelled) return;
        setSitePlansByProjectId((prev) => {
          const next = new Map(prev);
          for (const [projectId, sitePlan] of entries) {
            next.set(projectId, sitePlan);
          }
          return next;
        });
      })
      .finally(() => {
        if (!cancelled) setSitePlansLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, visualizerMode, columns, sitePlansByProjectId]);

  const sitePlanCompareColumns = useMemo<PublicSitePlanCompareColumn[]>(
    () =>
      columns.map((col) => {
        const projectId = col.unit.projectId ?? '';
        const hasFetched = projectId ? sitePlansByProjectId.has(projectId) : true;
        return {
          unit: col.unit,
          sitePlan: projectId ? sitePlansByProjectId.get(projectId) ?? null : null,
          loading: Boolean(projectId) && !hasFetched && sitePlansLoading,
        };
      }),
    [columns, sitePlansByProjectId, sitePlansLoading],
  );

  /**
   * Pre-compute the "best" numeric per comparable row so the cell
   * renderer can highlight winners without re-deriving on every cell.
   * Price/PSF require ALL columns to be numeric (every unit revealed)
   * before declaring a winner — otherwise a single revealed cell would
   * unfairly "win" against masked siblings.
   */
  const bestByRow = useMemo<Partial<Record<RowKey, number | null>>>(() => {
    const out: Partial<Record<RowKey, number | null>> = {};
    for (const key of COMPARABLE_KEYS) {
      const nums = columns.map((c) => cellNumeric(c, key));
      const allNumeric = nums.every((n) => n != null);
      const requireAll =
        key === 'price' || key === 'psf' || key === 'outlay' || key === 'installment';
      if (requireAll && !allNumeric) {
        out[key] = null;
        continue;
      }
      const valid = nums.filter((n): n is number => n != null);
      if (valid.length < 2) {
        out[key] = null;
        continue;
      }
      out[key] = BIGGER_BETTER[key] ? Math.max(...valid) : Math.min(...valid);
    }
    return out;
  }, [columns]);

  const columnCount = Math.min(columns.length, 3);
  const tripleColumnFit = columnCount === 3;
  const isVisualizer =
    expandedView || visualizerMode === 'siteplan';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: isMobile ? '100dvh' : '92vh',
          bgcolor: 'var(--color-surface)',
          color: 'rgba(var(--color-text-rgb), 0.9)',
          border: '1px solid rgba(var(--color-text-rgb), 0.08)',
          overflowX: 'hidden',
          width: '100%',
        },
      }}
    >
      <DialogTitle
        sx={{
          color: 'var(--color-text-strong)',
          fontWeight: 600,
          borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
          px: isMobile ? 1.5 : 3,
          py: isMobile ? 1.25 : 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            minWidth: 0,
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
            <Typography
              variant={isMobile ? 'subtitle1' : 'h6'}
              sx={{ color: 'inherit', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 1 }}
            >
              {isVisualizer
                ? visualizerMode === 'siteplan'
                  ? 'Site Plan Comparison Visualizer'
                  : 'Floor Plan Comparison Visualizer'
                : `Floor Plan Comparison (${cart.length})`}
            </Typography>
            <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(var(--color-text-rgb), 0.9)', flexShrink: 0 }}>
              <CloseIcon />
            </IconButton>
          </Box>
          {((!isVisualizer && visualizerMode === 'floorplan') ||
            visualizerMode === 'siteplan' ||
            isMobile ||
            !isVisualizer) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              flexWrap: 'wrap',
              minWidth: 0,
              width: '100%',
              justifyContent: isMobile ? 'flex-start' : 'flex-end',
            }}
          >
            {!isVisualizer && visualizerMode === 'floorplan' && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setFloorPlanZoom((z) => Math.max(1, Number((z - 0.1).toFixed(2))))}
                >
                  -
                </Button>
                <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'center' }}>
                  {Math.round(floorPlanZoom * 100)}%
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setFloorPlanZoom((z) => Math.min(3, Number((z + 0.1).toFixed(2))))}
                >
                  +
                </Button>
                <Button size="small" variant="text" onClick={() => setFloorPlanZoom(1)}>
                  Reset
                </Button>
              </>
            )}
            {visualizerMode === 'siteplan' && (
              <FormControlLabel
                sx={{ ml: 0, mr: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={showSitePlanPoints}
                    onChange={(e) => setShowSitePlanPoints(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Show Points
                  </Typography>
                }
              />
            )}
            {/* Mobile: chips above the pencil. Desktop/tablet table view: chips here. */}
            {(isMobile || !isVisualizer) &&
              columns.map((col, index) => (
                <Chip
                  key={col.unit.unitKey}
                  label={`${col.unit.projectName} ${col.unit.unitNumber}`}
                  onDelete={() => onRemove(col.unit)}
                  color={index === 0 ? 'primary' : 'secondary'}
                  size="small"
                  sx={{ maxWidth: isMobile ? '100%' : 220 }}
                />
              ))}
          </Box>
          )}
          {/* Desktop/tablet expanded/site-plan: chips share a full-width row with the pencil. */}
          {isVisualizer && (
            <Box
              sx={{
                display: 'flex',
                gap: 0.75,
                alignItems: 'center',
                flexWrap: 'nowrap',
                minWidth: 0,
                width: '100%',
                justifyContent: isMobile ? 'flex-start' : 'flex-end',
                overflowX: 'auto',
              }}
            >
              {!isMobile &&
                columns.map((col, index) => (
                  <Chip
                    key={col.unit.unitKey}
                    label={`${col.unit.projectName} ${col.unit.unitNumber}`}
                    onDelete={() => onRemove(col.unit)}
                    color={index === 0 ? 'primary' : 'secondary'}
                    size="small"
                    sx={{ maxWidth: 220, flexShrink: 0 }}
                  />
                ))}
              <FullVersionTooltip>
                  <span>
                    <IconButton size="small" disabled aria-label="Draw" sx={{ ...GATED_DISABLED_SX, flexShrink: 0 }}>
                      <PenIcon fontSize="small" />
                    </IconButton>
                  </span>
                </FullVersionTooltip>
            </Box>
          )}
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: isMobile ? 1.5 : 2,
          overflow: 'auto',
          overflowX: 'hidden',
          bgcolor: 'var(--color-surface)',
          borderColor: 'rgba(var(--color-text-rgb), 0.08)',
        }}
      >
        {cart.length === 0 ? (
          <Box sx={{ minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
              No units in your compare cart yet. Add up to 3 units from the
              tower view or available units to compare them side-by-side.
            </Typography>
          </Box>
        ) : visualizerMode === 'siteplan' ? (
          <SitePlanComparisonViewer
            columns={sitePlanCompareColumns}
            quickRefColumns={quickRefColumns}
            showPoints={showSitePlanPoints}
          />
        ) : expandedView ? (
          <FloorPlanComparisonViewer
            floorPlans={viewerFloorPlans}
            quickRefColumns={quickRefColumns}
          />
        ) : (
          isMobile ? (
            <Box sx={{ overflowX: 'auto', width: '100%', minWidth: 0, WebkitOverflowScrolling: 'touch' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `72px repeat(${columnCount}, minmax(132px, 1fr))`,
                  minWidth: Math.max(320, 72 + columnCount * 132),
                  border: '1px solid rgba(var(--color-text-rgb), 0.12)',
                  borderRadius: 1.5,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    bgcolor: 'var(--color-surface)',
                    borderRight: '1px solid rgba(var(--color-text-rgb), 0.12)',
                  }}
                />
                {columns.map((col, index) => (
                  <Box
                    key={`cmp-mobile-head-${col.unit.unitKey}`}
                    sx={{
                      p: 1,
                      borderRight: '1px solid rgba(var(--color-text-rgb), 0.12)',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', gap: 0 }}>
                        <FullVersionTooltip>
                          <span>
                            <IconButton size="small" disabled aria-label="Move left" sx={{ ...GATED_DISABLED_SX, p: 0.25 }}>
                              <ArrowBackIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </FullVersionTooltip>
                        <FullVersionTooltip>
                          <span>
                            <IconButton size="small" disabled aria-label="Move right" sx={{ ...GATED_DISABLED_SX, p: 0.25 }}>
                              <ArrowForwardIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </FullVersionTooltip>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => onRemove(col.unit)}
                        aria-label={`Remove ${col.unit.unitNumber} from comparison`}
                        sx={{ p: 0.25 }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                    <Box
                      sx={{
                        height: 110,
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: 'rgba(var(--color-text-rgb), 0.06)',
                        mb: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 0.5,
                        cursor: col.unit.floorplan ? 'pointer' : 'default',
                      }}
                      onClick={() => {
                        if (!col.unit.floorplan) return;
                        setExpandedView(true);
                      }}
                    >
                      {col.unit.floorplan ? (
                        <Box
                          component="img"
                          src={col.unit.floorplan}
                          alt={`Floor plan for ${col.unit.projectName} ${col.unit.unitNumber}`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            transform: `scale(${floorPlanZoom})`,
                            transformOrigin: 'center center',
                            transition: 'transform 0.15s ease',
                          }}
                        />
                      ) : (
                        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                          No image
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        textAlign: 'center',
                        display: 'block',
                        lineHeight: 1.2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {col.unit.projectName || '-'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'center', lineHeight: 1.2 }}>
                      {col.unit.unitNumber || '-'}
                    </Typography>
                    {col.isSold && (
                      <Typography
                        component="span"
                        sx={{ display: 'block', textAlign: 'center', fontSize: '0.65em', color: 'var(--color-danger)', fontWeight: 700 }}
                      >
                        SOLD
                      </Typography>
                    )}
                  </Box>
                ))}

                <Box
                  sx={{
                    gridColumn: `1 / span ${columnCount + 1}`,
                    px: 1,
                    py: 0.75,
                    bgcolor: 'rgba(var(--color-text-rgb), 0.08)',
                    borderTop: '1px solid rgba(var(--color-text-rgb), 0.12)',
                    borderBottom: '1px solid rgba(var(--color-text-rgb), 0.12)',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
                    OVERVIEW
                  </Typography>
                </Box>

                {OVERVIEW_ROWS.map((row) => {
                  const target = bestByRow[row.key] ?? null;
                  return (
                    <React.Fragment key={`cmp-mobile-row-${row.key}`}>
                      <Box
                        sx={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 1,
                          px: 0.75,
                          py: 1,
                          bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
                          borderTop: '1px solid rgba(var(--color-text-rgb), 0.12)',
                          borderRight: '1px solid rgba(var(--color-text-rgb), 0.12)',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                          {row.label}
                        </Typography>
                      </Box>
                      {columns.map((col, idx) => {
                        const display = cellDisplay(col, row.key);
                        const numeric = cellNumeric(col, row.key);
                        const isBest = target != null && numeric != null && numeric === target;
                        return (
                          <Box
                            key={`cmp-mobile-row-${row.key}-${idx}`}
                            sx={{
                              px: 0.75,
                              py: 1,
                              borderTop: '1px solid rgba(var(--color-text-rgb), 0.12)',
                              borderRight: '1px solid rgba(var(--color-text-rgb), 0.12)',
                              textAlign: 'center',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: isBest ? 700 : 500,
                                color: isBest
                                  ? 'var(--color-price-decrease, #2e7d32)'
                                  : 'var(--color-text-strong)',
                                lineHeight: 1.2,
                              }}
                            >
                              {display}
                            </Typography>
                          </Box>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>
          ) : (
          <Box sx={{ overflowX: tripleColumnFit ? 'hidden' : 'auto', width: '100%', minWidth: 0 }}>
            <Box
              sx={{
                width: '100%',
                minWidth: tripleColumnFit ? 0 : Math.max(760, columnCount * 260 + 220),
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `220px repeat(${columnCount}, minmax(0, 1fr))`,
                  border: '1px solid rgba(var(--color-text-rgb), 0.12)',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.04)', borderRight: '1px solid rgba(var(--color-text-rgb), 0.12)' }} />
                {columns.map((col, index) => (
                  <Box
                    key={`cmp-head-${col.unit.unitKey}`}
                    sx={{
                      p: 2,
                      borderRight: '1px solid rgba(var(--color-text-rgb), 0.12)',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <FullVersionTooltip>
                          <span>
                            <IconButton size="small" disabled aria-label="Move left" sx={GATED_DISABLED_SX}>
                              <ArrowBackIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </FullVersionTooltip>
                        <FullVersionTooltip>
                          <span>
                            <IconButton size="small" disabled aria-label="Move right" sx={GATED_DISABLED_SX}>
                              <ArrowForwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </FullVersionTooltip>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => onRemove(col.unit)}
                        aria-label={`Remove ${col.unit.unitNumber} from comparison`}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box
                      sx={{
                        height: 140,
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: 'rgba(var(--color-text-rgb), 0.06)',
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1,
                        cursor: col.unit.floorplan ? 'pointer' : 'default',
                      }}
                      onClick={() => {
                        if (!col.unit.floorplan) return;
                        setExpandedView(true);
                      }}
                    >
                      {col.unit.floorplan ? (
                        <Box
                          component="img"
                          src={col.unit.floorplan}
                          alt={`Floor plan for ${col.unit.projectName} ${col.unit.unitNumber}`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            maxWidth: '100%',
                            maxHeight: '100%',
                            transform: `scale(${floorPlanZoom})`,
                            transformOrigin: 'center center',
                            transition: 'transform 0.15s ease',
                          }}
                        />
                      ) : (
                        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                          No image
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'center' }}>
                      {col.unit.projectName || '-'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'center' }}>
                        {col.unit.unitNumber || '-'}
                      </Typography>
                      {col.isSold && (
                        <Typography
                          component="span"
                          sx={{ fontSize: '0.75em', color: 'var(--color-danger)', fontWeight: 700 }}
                        >
                          SOLD
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}

                <Box
                  sx={{
                    gridColumn: `1 / span ${columnCount + 1}`,
                    p: 1.5,
                    bgcolor: 'rgba(var(--color-text-rgb), 0.08)',
                    borderTop: '1px solid rgba(var(--color-text-rgb), 0.12)',
                    borderBottom: '1px solid rgba(var(--color-text-rgb), 0.12)',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
                    OVERVIEW
                  </Typography>
                </Box>

                {OVERVIEW_ROWS.map((row) => {
                  const target = bestByRow[row.key] ?? null;
                  return (
                    <React.Fragment key={`cmp-row-${row.key}`}>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
                          borderTop: '1px solid rgba(var(--color-text-rgb), 0.12)',
                          fontWeight: 600,
                        }}
                      >
                        {row.label}
                      </Box>
                      {columns.map((col, idx) => {
                        const display = cellDisplay(col, row.key);
                        const numeric = cellNumeric(col, row.key);
                        const isBest =
                          target != null && numeric != null && numeric === target;
                        return (
                          <Box
                            key={`cmp-row-${row.key}-${idx}`}
                            sx={{
                              p: 1.5,
                              borderTop: '1px solid rgba(var(--color-text-rgb), 0.12)',
                              borderRight: '1px solid rgba(var(--color-text-rgb), 0.12)',
                              textAlign: 'center',
                              fontWeight: isBest ? 700 : 500,
                              color: isBest
                                ? 'var(--color-price-decrease, #2e7d32)'
                                : 'var(--color-text-strong)',
                            }}
                          >
                            {display}
                          </Box>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>
          </Box>
          )
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: isMobile ? 1.5 : 2,
          py: isMobile ? 1.25 : 1,
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: 'nowrap',
          gap: 1,
          justifyContent: isMobile ? 'stretch' : 'flex-start',
          alignItems: isMobile ? 'stretch' : 'center',
          borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)',
          overflowX: isMobile ? undefined : 'auto',
        }}
      >
        {isMobile ? (
          <>
            {isVisualizer && (
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <VisualizerModeToggle value={visualizerMode} onChange={setVisualizerMode} />
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1, width: '100%', '& > *': { flex: '1 1 0', minWidth: 0 } }}>
              <Button
                size="small"
                variant="outlined"
                disabled
                sx={{
                  ...GATED_DISABLED_ACCENT_SX,
                  flexDirection: 'column',
                  gap: 0.25,
                  py: 0.75,
                  borderRadius: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PictureAsPdfIcon fontSize="small" />
                  <Typography component="span" variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    Export PDF
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.6rem',
                    lineHeight: 1.2,
                    color: 'rgba(var(--color-text-rgb), 0.65)',
                  }}
                >
                  {FULL_VERSION_CAPTION}
                </Typography>
              </Button>
              <Button size="small" onClick={onClear} disabled={cart.length === 0}>
                Clear All
              </Button>
            </Box>
            <Button
              size="small"
              fullWidth
              variant={isVisualizer ? 'contained' : 'outlined'}
              onClick={() => {
                if (visualizerMode === 'siteplan') {
                  setVisualizerMode('floorplan');
                  setExpandedView(false);
                  return;
                }
                setExpandedView((prev) => !prev);
              }}
              disabled={cart.length === 0}
            >
              {isVisualizer ? 'Table View' : 'Expanded Compare'}
            </Button>
            <Button size="small" fullWidth onClick={onClose} variant="contained">
              Close
            </Button>
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              gap: 1,
              flexWrap: 'nowrap',
              minWidth: 0,
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <Button
                size="small"
                variant={isVisualizer ? 'contained' : 'outlined'}
                onClick={() => {
                  if (visualizerMode === 'siteplan') {
                    setVisualizerMode('floorplan');
                    setExpandedView(false);
                    return;
                  }
                  setExpandedView((prev) => !prev);
                }}
                disabled={cart.length === 0}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {isVisualizer ? 'Table View' : 'Expanded Compare'}
              </Button>
            </Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                minWidth: 0,
                px: 0.5,
              }}
            >
              {isVisualizer && (
                <VisualizerModeToggle value={visualizerMode} onChange={setVisualizerMode} />
              )}
            </Box>
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Button
                size="small"
                variant="outlined"
                disabled
                sx={{
                  ...GATED_DISABLED_ACCENT_SX,
                  flexDirection: 'column',
                  gap: 0.25,
                  py: 0.75,
                  borderRadius: 1.5,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PictureAsPdfIcon fontSize="small" />
                  <Typography component="span" variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    Export PDF
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.6rem',
                    lineHeight: 1.2,
                    color: 'rgba(var(--color-text-rgb), 0.65)',
                  }}
                >
                  {FULL_VERSION_CAPTION}
                </Typography>
              </Button>
              <Button
                size="small"
                onClick={onClear}
                disabled={cart.length === 0}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                Clear All
              </Button>
              <Button
                size="small"
                onClick={onClose}
                variant="contained"
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                Close
              </Button>
            </Box>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FloorPlanCompareModal;
